import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import { CheckStatusRechargeFlowService, HiddenHistory, RechargeAllService, RechargeByTypeService, RechargeCreateService, ResentRechargeService, SetStatusRechargeFlowService } from "./RechargeService"
import useAuthContext from "../../hooks/useAuthContext";
import { useToast } from "@chakra-ui/toast";

const queryRecharge = () => {
  const queryClient = useQueryClient()
  const { user } = useAuthContext()
  const toast = useToast();
  
  const listRechargeByType = (type) => {
    const { data, isLoading, isError, error, ...rest } = useQuery(
      ['listRechargeByType', type],
      () => RechargeByTypeService(type),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data

    return { data: dataResponse, isLoading, isError, error, ...rest }
  }

  const rechargeCreate = (data) => {
    const { data: res, isLoading, isError, error } = useQuery(
      ['rechargeCreate', data],
      () => {
        if (!data?.currency || !data?.contacts?.length) return {data:{data: null}}
        return RechargeCreateService({token: user.access_token, ...data})},
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        // enabled,
      }, 
    )

    const dataResponse = res?.data.data

    return { data: dataResponse, isLoading, isError, error }
  }

  const listRechargeAll = ({ limit = 10, search = '', orderField = '', orderBy = '' }) => {
    const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } = useInfiniteQuery(
      ['listRechargeAll', limit, search, orderField, orderBy],
      ({ pageParam = 1 }) => RechargeAllService({ token: user.access_token, page: pageParam, limit, search, orderField, orderBy }),
      {
        getNextPageParam: (lastPage) => {
          const meta = lastPage?.data?.data?.meta;
          return meta?.current_page < meta?.last_page ? meta.current_page + 1 : undefined;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 1,
      }
    );

    const allItems = data?.pages?.flatMap(p => p?.data?.data?.data || []) ?? [];
    const lastMeta = data?.pages?.at(-1)?.data?.data?.meta;

    return { data: { data: allItems, meta: lastMeta }, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error, ...rest };
  };

  const resentRecharge = () => {
    const { data, isLoading, ...rest } = useMutation((ids) => ResentRechargeService({
      token: user.access_token,
      ids,
    }), {
      onSuccess: (_, ids) => {
        queryClient.invalidateQueries({ queryKey: ['listRechargeAll'] })
        queryClient.invalidateQueries({ queryKey: ['listRechargeDenied'] })
        toast({
          title: ids.length > 1 ? "Recargas reenviadas" : "Recarga reenviada",
          description: ids.length > 1 
            ? `Las ${ids.length} recargas se han reenviado correctamente.`
            : `La recarga se ha reenviado correctamente.`,
          status: "success",
          duration: 3000,
          position: "top-right",
          isClosable: true,
        })
      },
      onError: () => {
        toast({
          title: "Error al reenviar recargas",
          description: "Ha ocurrido un error al reenviar las recargas.",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        })
      },
    })

    const dataResponse = data?.data.data;

    return { data: dataResponse, isLoading, ...rest };
  };
  
  const checkStatusRechargeFlow = () => {
    const { data, isLoading, ...rest} = useQuery(
      ['statusScheduledRecharge'],
      () => CheckStatusRechargeFlowService(user.access_token),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 1, // 5 minutes
        keepPreviousData: true,
      }
    )

    const dataResponse = data?.data.data;

    return { data: dataResponse, isLoading, ...rest };
  }

  const setStatusRechargeFlow = () => {
    const {mutate, isLoading, ...rest} = useMutation(
      (status) => SetStatusRechargeFlowService({token: user.access_token, status}),
      {
        onSuccess: (data) => {
          if (data?.data.data?.status) {
            toast({
              title: "Flujo de recargas detenido",
              description: "Todas las recargas en cola serán detenidas.",
              status: "warning",
              duration: 2000,
              isClosable: true,
              position: "top-right",
            })
          } else {
            toast({
              title: "Flujo de recargas iniciado",
              description: "Las recargas en cola serán procesadas.",
              status: "success",
              duration: 2000,
              isClosable: true,
              position: "top-right",            
            })
          }
          const oldData = queryClient.getQueryData(['statusScheduledRecharge'])
          queryClient.setQueryData(['statusScheduledRecharge'], {
            ...oldData,
            data: {
              ...oldData?.data,
              data: {
                ...oldData?.data?.data,
                status: data?.data.data?.status,
              }
            }
          })
        },
        onError: () => {
          toast({
            title: "Error al cambiar el estado del flujo de recargas",
            description: "Ha ocurrido un error al cambiar el estado del flujo de recargas.",
            status: "error",
            duration: 2000,
            isClosable: true,
            position: "top-right",
          })
        },
      }
    )

    return { mutate, isLoading, ...rest };    
  }
  
  const hiddenRecharge = () => {
    const mutation = useMutation(() => HiddenHistory({token: user.access_token}), {
      onSuccess: () => {
        queryClient.removeQueries(['contactsRechargesByUser'])
        toast({
          title: "Historial de recargas limpiado",
          description: "El historial de recargas se ha limpiado correctamente.",
          status: "success",
          duration: 3000,
          position: "top-right",
          isClosable: true,
        })
      },
      onError: () => {
        toast({
          title: "Error al limpiar el historial de recargas",
          description: "Ha ocurrido un error al limpiar el historial de recargas.",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        })
      },
    })

    return mutation;
  }
  
  return { listRechargeByType, rechargeCreate, listRechargeAll, resentRecharge, checkStatusRechargeFlow, setStatusRechargeFlow, hiddenRecharge };
};

export default queryRecharge